/** Knowledge suggestion card — SPEC-2 §3.1 and §6. */

import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  FileText,
  Lightbulb,
  ListTodo,
  NotebookPen,
  Sparkles,
  TriangleAlert,
} from "lucide-react";

import type { HitOut, SearchEntityType } from "@/api/types";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { formatRelativePast, parseDate } from "@/lib/format";

const TYPE_META: Record<
  SearchEntityType,
  { label: string; icon: typeof BookOpen; route: (id: string) => string | null }
> = {
  method: { label: "метод", icon: BookOpen, route: (id) => `/knowledge/${id}` },
  method_version: {
    label: "версия метода",
    icon: BookOpen,
    // A superseded version has no screen of its own — it lives inside its
    // method's Версии tab, which is where this sends you.
    route: (id) => `/knowledge/sources/method_version/${id}`,
  },
  task: { label: "задача", icon: ListTodo, route: (id) => `/tasks/${id}` },
  idea: { label: "идея", icon: Lightbulb, route: (id) => `/knowledge/sources/idea/${id}` },
  work_log_entry: { label: "запись журнала", icon: NotebookPen, route: (id) => `/knowledge/sources/work_log_entry/${id}` },
  note: { label: "материал", icon: FileText, route: (id) => `/knowledge/sources/note/${id}` },
};

/**
 * Build the "почему это здесь" line (SPEC-2 §3.1).
 *
 * A suggestion without a reason is a guess the person cannot check, and after
 * the second wrong guess they stop looking at the block entirely. So the
 * reason is not optional decoration — it is the thing that keeps the feature
 * usable, and it is assembled from what the ranker actually did rather than
 * from a template.
 */
export function reasonLine(hit: HitOut): string {
  const parts: string[] = [];
  const terms = hit.reason.fts_terms
    ?.replace(/<<([^>]*)>>/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (hit.reason.fts_terms?.includes("<<") && terms) parts.push(`совпало: ${terms}`);
  if (hit.reason.semantic) parts.push("смысловое");
  if (hit.reason.tags.length) parts.push(`тег ${hit.reason.tags.join(", ")}`);
  if (hit.reason.project) parts.push("этот проект");
  return parts.join(" · ");
}

/** "применён 3 раза, 2 успешно" — empty for anything that is not a method. */
export function experienceLine(hit: HitOut): string | null {
  const { applications, success } = hit.reason.experience;
  if (applications <= 0) return null;
  return `применён ${applications} ${applications === 1 ? "раз" : "раза"}, ${success} успешно`;
}

export interface SuggestionCardProps {
  hit: HitOut;
  onApply?: () => void;
  onDismiss?: () => void;
  applying?: boolean;
  className?: string;
}

export function SuggestionCard({
  hit,
  onApply,
  onDismiss,
  applying,
  className,
}: SuggestionCardProps) {
  const navigate = useNavigate();
  const meta = TYPE_META[hit.entity_type];
  const Icon = meta.icon;
  const route = meta.route(hit.entity_id);
  const reason = reasonLine(hit);
  const experience = experienceLine(hit);
  const freshness = parseDate(hit.freshness);

  return (
    <Card className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-micro uppercase tracking-[.06em] text-hint">{meta.label}</p>
          <p className="text-cell font-medium text-text">{hit.title}</p>
        </div>
        {hit.reason.flags.needs_review && (
          <Badge tone="danger" icon={<TriangleAlert className="h-3 w-3" aria-hidden="true" />}>
            требует пересмотра
          </Badge>
        )}
      </div>

      {hit.reason.flags.erroneous && <Badge tone="danger">Ошибочный подход</Badge>}
      {hit.reason.flags.superseded && <Badge tone="neutral">Историческая версия</Badge>}

      {reason && (
        <p className="flex items-start gap-1.5 text-meta text-hint">
          <Sparkles className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
          <span>{reason}</span>
        </p>
      )}

      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-meta text-hint">
        {freshness && <span>обновлён {formatRelativePast(freshness)}</span>}
        {experience && <span>{experience}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-0.5">
        {route && (
          <Button size="compact" variant="secondary" onClick={() => navigate(route)}>
            Посмотреть
          </Button>
        )}
        {onApply && hit.entity_type === "method" && (
          <Button size="compact" variant="strong" onClick={onApply} loading={applying}>
            Применить
          </Button>
        )}
        {onDismiss && (
          <Button size="compact" variant="ghost" onClick={onDismiss}>
            Не подходит
          </Button>
        )}
      </div>
    </Card>
  );
}
